"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

export interface PortaledMenuProps {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Optional ref to the portaled menu panel (keyboard roving). */
  menuRef?: React.RefObject<HTMLDivElement | null>;
  /** Which edge of the trigger the menu aligns to. */
  align?: "left" | "right";
  width?: number;
  className?: string;
  children: React.ReactNode;
}

/**
 * Dropdown menu portaled to document.body with fixed coordinates so it is
 * never clipped by overflow-y ancestors (chat thread, matches list).
 * Flips above the trigger when there is not enough space below.
 */
export function PortaledMenu({
  open,
  onClose,
  triggerRef,
  menuRef: menuRefProp,
  align = "left",
  width = 176,
  className,
  children,
}: PortaledMenuProps) {
  const internalMenuRef = useRef<HTMLDivElement>(null);
  const menuRef = menuRefProp ?? internalMenuRef;
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setPos(null);
      return;
    }

    const update = () => {
      const trigger = triggerRef.current;
      // The menu renders invisibly (pos === null) before this runs, so its
      // real height is always measurable — never estimate it, or a menu
      // taller than the guess overflows the viewport bottom.
      const menuHeight = menuRef.current?.offsetHeight;
      if (!trigger || menuHeight === undefined) return;
      const rect = trigger.getBoundingClientRect();
      const viewportBottom = window.visualViewport?.height ?? window.innerHeight;
      const spaceBelow = viewportBottom - rect.bottom;
      const openAbove = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8;
      const top = openAbove ? rect.top - menuHeight - 4 : rect.bottom + 4;
      const left = align === "right" ? rect.right - width : rect.left;
      setPos({
        top: Math.max(8, top),
        left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
      });
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, [open, triggerRef, align, width, menuRef]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, triggerRef, menuRef]);

  if (!open || typeof document === "undefined") return null;

  // First pass renders the menu invisibly at the viewport origin so the
  // layout effect above can measure its real height; the second pass (pos
  // set) shows it at the computed spot.
  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      style={{
        position: "fixed",
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        width,
        zIndex: 50,
        visibility: pos ? "visible" : "hidden",
      }}
      className={cn(className)}
    >
      {children}
    </div>,
    document.body,
  );
}
